import { createSlice, createAsyncThunk, PayloadAction, createSelector } from '@reduxjs/toolkit';
import { DashboardEvent } from '../../services/dashboardService';
import { logger } from "../../utils/logger";
import {
    normalizeDashboardEventRows,
    normalizeDashboardTasks,
    normalizeDashboardTextRow,
    mapTrainingManagerStats,
} from '../../../shared/utils/coerceEventListingText';
// Types
interface TrainingManagerStats {
    total_events: number;
    upcoming_events: number;
    completed_events: number;
    total_participants: number;
    average_rating: number;
    completion_rate: number;
    total_revenue: number;
    pending_tasks: number;
    assigned_instructors: number;
}
interface EventTask {
    id: number;
    title: string;
    description: string;
    status: 'pending' | 'in_progress' | 'completed';
    priority: 'low' | 'medium' | 'high';
    due_date: string;
    assigned_to?: string;
}
interface InstructorAssignment {
    id: number;
    instructor_name: string;
    instructor_email: string;
    event_title: string;
    event_date: string;
    status: 'assigned' | 'confirmed' | 'completed';
    rating?: number;
}
interface TrainingState {
    myEvents: DashboardEvent[];
    eventTasks: EventTask[];
    instructorAssignments: InstructorAssignment[];
    trainingManagerStats: TrainingManagerStats | null;
    loading: boolean;
    statsLoading: boolean;
    eventsLoading: boolean;
    tasksLoading: boolean;
    assignmentsLoading: boolean;
    error: string | null;
}
// Initial state
const initialState: TrainingState = {
    myEvents: [],
    eventTasks: [],
    instructorAssignments: [],
    trainingManagerStats: null,
    loading: true,
    statsLoading: true,
    eventsLoading: true,
    tasksLoading: true,
    assignmentsLoading: true,
    error: null,
};
// Async thunks
export const loadTrainingData = createAsyncThunk('training/loadTrainingData', async (_, { rejectWithValue }) => {
    try {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };
        // Load data with parallel API calls for better performance
        const [statsResult, eventsResult, tasksResult, assignmentsResult] = await Promise.allSettled([
            fetch('/api/dashboard/training-manager/stats', { headers, credentials: 'include' }),
            fetch('/api/dashboard/training-manager/events', { headers, credentials: 'include' }),
            fetch('/api/dashboard/training-manager/tasks', { headers, credentials: 'include' }),
            fetch('/api/dashboard/training-manager/instructor-assignments', { headers, credentials: 'include' })
        ]);
        // Process results
        let stats: TrainingManagerStats = {
            total_events: 0,
            upcoming_events: 0,
            completed_events: 0,
            total_participants: 0,
            average_rating: 0,
            completion_rate: 0,
            total_revenue: 0,
            pending_tasks: 0,
            assigned_instructors: 0
        };
        let events: DashboardEvent[] = [];
        let tasks: EventTask[] = [];
        let assignments: InstructorAssignment[] = [];
        // Process stats
        if (statsResult.status === 'fulfilled' && statsResult.value.ok) {
            const statsData = await statsResult.value.json();
            if (statsData.success) {
                stats = mapTrainingManagerStats(statsData.data);
            }
        }
        // Process events
        if (eventsResult.status === 'fulfilled' && eventsResult.value.ok) {
            const eventsData = await eventsResult.value.json();
            if (eventsData.success) {
                // The API returns { data: { events: [...], pagination: {...} } }
                // Handle both structures for backwards compatibility
                let rawEvents: any[] = [];
                if (Array.isArray(eventsData.data)) {
                    rawEvents = eventsData.data;
                }
                else if (eventsData.data && Array.isArray(eventsData.data.events)) {
                    rawEvents = eventsData.data.events;
                }
                else {
                    rawEvents = [];
                }
                // Transform events to match DashboardEvent interface
                const mappedEvents = rawEvents.map((event: any) => {
                    // Debug: Log what we're receiving from API
                    logger.debug(`[Training Slice] Event ${event.id}: max_registrants=${event.max_registrants}, max_participants=${event.max_participants}, capacity=${event.capacity}`);
                    const maxRegistrants = event.max_registrants != null
                        ? Number(event.max_registrants)
                        : (event.max_participants != null
                            ? Number(event.max_participants)
                            : (event.capacity != null
                                ? Number(event.capacity)
                                : 0));
                    logger.debug(`[Training Slice] Event ${event.id}: Calculated maxRegistrants=${maxRegistrants}`);
                    return {
                        id: event.id,
                        title: event.title || 'Untitled Event',
                        slug: event.slug,
                        description: event.description || '',
                        startDate: event.start_datetime || event.startDate || '',
                        endDate: event.end_datetime || event.endDate || '',
                        venueName: event.venue_name || '',
                        venueCity: event.venue_city || '',
                        venueState: event.venue_state || '',
                        venueAddress: event.venue_address || '',
                        mode: event.mode || 'in_person',
                        status: event.status === 2 || event.status === 'published' ? 'published' : 'draft',
                        isRegistered: false,
                        maxRegistrants: maxRegistrants,
                        currentRegistrants: event.registered_count || event.currentRegistrants || 0,
                        cost: event.price || 0,
                        image: event.image || event.main_image,
                        category: event.category_name || 'Training',
                        isFeatured: event.is_featured || event.featured || false,
                        start_date: event.start_datetime || event.startDate || '',
                        end_date: event.end_datetime || event.endDate || '',
                        is_canceled: event.is_canceled || false,
                        isCanceled: event.is_canceled || false,
                        // Add instructor and image data
                        instructorName: event.instructor_name || null,
                        instructorImage: event.instructor_image || null,
                    };
                });
                events = normalizeDashboardEventRows(mappedEvents);
            }
        }
        // Process tasks
        if (tasksResult.status === 'fulfilled' && tasksResult.value.ok) {
            const tasksData = await tasksResult.value.json();
            if (tasksData.success) {
                const rawTasks = Array.isArray(tasksData.data) ? tasksData.data : [];
                tasks = normalizeDashboardTasks(rawTasks) as EventTask[];
            }
        }
        // Process assignments
        if (assignmentsResult.status === 'fulfilled' && assignmentsResult.value.ok) {
            const assignmentsData = await assignmentsResult.value.json();
            if (assignmentsData.success) {
                const rawAssignments = Array.isArray(assignmentsData.data) ? assignmentsData.data : [];
                assignments = rawAssignments.map((row: Record<string, unknown>) =>
                    normalizeDashboardTextRow(row, ['instructor_name', 'event_title', 'instructor_email']),
                ) as InstructorAssignment[];
            }
        }
        return {
            stats,
            events,
            tasks,
            assignments,
        };
    }
    catch (error) {
        return rejectWithValue('Failed to load training data');
    }
});
// Slice
const trainingSlice = createSlice({
    name: 'training',
    initialState,
    reducers: {
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        resetTrainingState: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(loadTrainingData.pending, (state) => {
            state.loading = true;
            state.statsLoading = true;
            state.eventsLoading = true;
            state.tasksLoading = true;
            state.assignmentsLoading = true;
            state.error = null;
        })
            .addCase(loadTrainingData.fulfilled, (state, action) => {
            state.loading = false;
            state.statsLoading = false;
            state.eventsLoading = false;
            state.tasksLoading = false;
            state.assignmentsLoading = false;
            state.trainingManagerStats = action.payload.stats;
            state.myEvents = action.payload.events;
            state.eventTasks = action.payload.tasks;
            state.instructorAssignments = action.payload.assignments;
            state.error = null;
        })
            .addCase(loadTrainingData.rejected, (state, action) => {
            state.loading = false;
            state.statsLoading = false;
            state.eventsLoading = false;
            state.tasksLoading = false;
            state.assignmentsLoading = false;
            state.error = action.payload as string;
        });
    },
});
// Export actions
export const { setError, resetTrainingState } = trainingSlice.actions;
// Export selectors
export const selectTraining = (state: {
    training: TrainingState;
}) => state.training;
export const selectMyEvents = (state: {
    training: TrainingState;
}) => state.training.myEvents;
export const selectEventTasks = (state: {
    training: TrainingState;
}) => state.training.eventTasks;
export const selectInstructorAssignments = (state: {
    training: TrainingState;
}) => state.training.instructorAssignments;
export const selectTrainingStats = (state: {
    training: TrainingState;
}) => state.training.trainingManagerStats;
export const selectTrainingLoading = createSelector([(state: {
        training: TrainingState;
    }) => state.training], (training) => ({
    loading: training.loading,
    statsLoading: training.statsLoading,
    eventsLoading: training.eventsLoading,
    tasksLoading: training.tasksLoading,
    assignmentsLoading: training.assignmentsLoading,
}));
// Export reducer
export default trainingSlice.reducer;
